import { useEffect, useRef, useState, useCallback } from "react";
import {
  displayAdManager,
  SlotConfig,
  SlotSize,
  SizeMapping,
} from "MVVM/Utilities/Ads/DisplayAdManager";

interface DisplayAdConfig {
  networkId: string;
  adUnit: string;
}

interface UseDisplayAdOptions {
  slotId: string;
  sizes: SlotSize[];
  sizeMapping?: SizeMapping;
  displayConfig: DisplayAdConfig | null;
  game?: string;
  enabled?: boolean;
  onAdLoaded?: (hasAd: boolean, size?: number[] | string) => void;
  timeout?: number;
  allowProgrammatic?: boolean;
}

interface UseDisplayAdResult {
  containerRef: (node: HTMLDivElement | null) => void;
  hasAd: boolean;
  isLoading: boolean;
  isResolved: boolean;
  renderedSize: number[] | string | null;
}

export function useDisplayAd({
  slotId,
  sizes,
  sizeMapping,
  displayConfig,
  game,
  enabled = true,
  onAdLoaded,
  timeout = 3000,
  allowProgrammatic = false,
}: UseDisplayAdOptions): UseDisplayAdResult {
  const [hasAd, setHasAd] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolved, setIsResolved] = useState(false);
  const [domReady, setDomReady] = useState(false);
  const [renderedSize, setRenderedSize] = useState<number[] | string | null>(
    null,
  );

  const mountedRef = useRef(true);
  const slotCreatedRef = useRef(false);
  const callbackFiredRef = useRef(false);
  const onAdLoadedRef = useRef(onAdLoaded);

  onAdLoadedRef.current = onAdLoaded;

  const fireCallback = useCallback(
    (adLoaded: boolean, size?: number[] | string) => {
      if (callbackFiredRef.current || !mountedRef.current) return;
      callbackFiredRef.current = true;
      setHasAd(adLoaded);
      setIsLoading(false);
      setIsResolved(true);
      if (size) {
        setRenderedSize(size);
      }
      onAdLoadedRef.current?.(adLoaded, size);
    },
    [],
  );

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setDomReady(true);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    callbackFiredRef.current = false;
    slotCreatedRef.current = false;
    setHasAd(false);
    setIsLoading(true);
    setIsResolved(false);
  }, [slotId]);

  useEffect(() => {
    if (!enabled || !displayConfig) {
      fireCallback(false);
      return;
    }

    if (!domReady) {
      return;
    }

    if (slotCreatedRef.current) {
      return;
    }

    slotCreatedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const testAds = params.get("testAds");
    const rawPartner = params.get("platform") || "ALLSTAR";
    const partner = rawPartner.replace(/[^a-zA-Z0-9_-]/g, "_");
    const viewerSource = window.location.href.includes("iframe")
      ? "iframe"
      : "website";

    const targeting: Record<string, string> = {
      viewer_source: viewerSource,
      partner,
    };

    if (testAds) {
      targeting.test = testAds;
    }

    if (game) {
      targeting.game = game;
    }

    if (allowProgrammatic) {
      targeting.allow_programmatic = "true";
    }

    const config: SlotConfig = {
      adUnitPath: `/${displayConfig.networkId}/${displayConfig.adUnit}`,
      sizes,
      sizeMapping,
      targeting,
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    displayAdManager
      .createSlot(slotId, config, (event) => {
        if (!mountedRef.current) return;
        const adLoaded = !event.isEmpty;
        fireCallback(adLoaded, event.size);
      })
      .then((slot) => {
        if (!mountedRef.current) return;

        if (!slot) {
          fireCallback(false);
          return;
        }

        displayAdManager.refreshSlot(slotId);
        timeoutId = setTimeout(() => {
          fireCallback(false);
        }, timeout);
      })
      .catch(() => {
        if (mountedRef.current) {
          fireCallback(false);
        }
      });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    slotId,
    sizes,
    sizeMapping,
    displayConfig,
    game,
    enabled,
    domReady,
    timeout,
    fireCallback,
    allowProgrammatic,
  ]);

  useEffect(() => {
    return () => {
      displayAdManager.destroySlot(slotId);
    };
  }, [slotId]);

  return {
    containerRef,
    hasAd,
    isLoading,
    isResolved,
    renderedSize,
  };
}
